import { format } from 'date-fns';
import { ShowSummary } from '../types';

// Display info for the server-computed rarity rating (see
// backend/src/api/services/rarity_service.py for the formula: appearances vs
// scan-covered opportunities, shrunk toward the category average).
export interface RarityDisplay {
  stars: number | null; // null = not rated (upcoming / unknown schedule)
  tooltip: string;
  provisional?: boolean;
  trend?: 'up' | 'down' | null;
  trendSince?: string | null;
}

// Plain-language meaning of each star level (1=common .. 5=rare).
const STAR_MEANINGS: Record<number, string> = {
  1: 'Almost always at TKTS',
  2: 'Usually at TKTS',
  3: 'Hit or miss at TKTS',
  4: 'Rarely at TKTS',
  5: 'Almost never at TKTS',
};

const fmtSince = (iso: string | null | undefined): string =>
  iso ? `since ${format(new Date(iso + 'T12:00:00'), 'MMM d')}` : 'this week';

// Plain before/now framing, using the star level as the "before".
export const trendTooltip = (
  trend: 'up' | 'down',
  since?: string | null,
  stars?: number | null
): string => {
  const when = fmtSince(since);
  if (trend === 'up') {
    return stars != null && stars >= 4
      ? `Deals for this show are usually rare, but it has been at TKTS consistently ${when} — a good time to catch it.`
      : `This show has been at TKTS more often than usual ${when}.`;
  }
  return stars != null && stars <= 2
    ? `This show usually has TKTS deals, but they have mostly stopped ${when}.`
    : `TKTS deals for this show have slowed down ${when}.`;
};

export const trendLabel = (trend: 'up' | 'down'): string =>
  trend === 'up' ? 'Suddenly available' : 'Drying up';

export const getRarityInfo = (show: ShowSummary): RarityDisplay => {
  const r = show.rarity;
  if (!r) {
    return { stars: null, tooltip: 'No rating available for this show.' };
  }

  if (r.status === 'unrated' || r.stars == null) {
    const openStr = show.preview_date || show.opening_date;
    const notOpenYet = openStr && new Date(openStr + 'T12:00:00') > new Date();
    return {
      stars: null,
      tooltip: notOpenYet
        ? `No rating yet — performances start ${format(new Date(openStr + 'T12:00:00'), 'MMM d, yyyy')}.`
        : "No rating yet — we don't know enough about this show's schedule.",
    };
  }

  const meaning = STAR_MEANINGS[r.stars];
  const periodTxt = r.period === 'full_run' ? 'while it was running' : 'in the last 4 weeks';
  const counts = `had deals for ${r.appearances} of its ~${Math.round(r.opportunities)} performances ${periodTxt}`;
  const tooltip = r.status === 'provisional'
    ? `${meaning} (early estimate) — ${counts}. This show is new to tracking, so the rating may shift.`
    : `${meaning} — ${counts}.`;

  return {
    stars: r.stars,
    tooltip,
    provisional: r.status === 'provisional',
    trend: r.trend,
    trendSince: r.trend_since,
  };
};
